import { useEffect, useState } from "react";

// Third Party
import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TableSkeleton from "@/components/skeleton/TableSkeleton";
import Pagination from "@/components/Pagination";
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogDescription,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import FileHandleViewer from "@/components/FileHandleViewer";
// Icons
import { PlusCircle, Save } from "lucide-react";

// Services
import {
  getCompanyShareholders,
  updateCompanyShareholder,
} from "@/services/soe";
import { getTenureStatus, getTenureLength } from "@/services/master";
import type { CompanyBoards } from "@/types/soe";
import { S3_BUCKET_LINK } from "@/components/constants";
import formatDate from "@/utils/formatDate";
import { FileUpload } from "@/components/FileUpload";
import { ErrorResponse } from "@/types/ErrorResponse";
import type { AxiosError } from "axios";
import { clearLocalStorage } from "@/helpers/accessLocalStorage";

export const Route = createFileRoute(
  "/_layout-private/account-management/soe/_information/board-of-directors/"
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      activeStatus: search.activeStatus as string | undefined,
      page: Number(search.page) || 1,
    };
  },
});
function RouteComponent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const theid = localStorage.getItem("companyId") || "";
  const companyId = theid.replace(/^"(.+)"$/, "$1");
  const { company_management } = useAccessPermission();

  const [selectedItem, setSelectedItem] = useState({} as CompanyBoards);
  const [editFormData, setEditFormData] = useState<{
    status: string;
    endDoc: string | null;
  }>({
    status: "",
    endDoc: null,
  });

  const [editTenureModal, setEditTenureModal] = useState(false);

  // Query
  const { data: tenureStatusRes, isLoading: tenureStatusLoading } = useQuery({
    queryKey: ["tenure-status"],
    queryFn: () => getTenureStatus(),
  });

  // const { data: shareholdersRes, isLoading: shareholdersLoading } = useQuery({
  // 	queryKey: ["shareholders", companyId, search.activeStatus, search.page],
  // 	queryFn: () =>
  // 		getCompanyShareholders({
  // 			companyId: companyId,
  // 			skip: 0,
  // 			limit: 40,
  // 			tenureStatusId: search.activeStatus as string,
  // 		}),
  // 	enabled: !!search.activeStatus,
  // });

  const [
    {
      data: shareholdersRes,
      isLoading: shareholdersLoading,
      error: shareholdersError,
    },
    { data: tenureLengthRes, error: tenureLengthError },
  ] = useQueries({
    queries: [
      {
        queryKey: ["shareholders", companyId, search.activeStatus, search.page],
        queryFn: () =>
          getCompanyShareholders({
            companyId: companyId,
            skip: 0,
            limit: 40,
            tenureStatusId: search.activeStatus as string,
          }),
        enabled: !!search.activeStatus,
      },
      {
        queryKey: ["tenure-length"],
        queryFn: () => getTenureLength(),
      },
    ],
  });

  if (shareholdersError || tenureLengthError) {
    const isAxiosError = (
      error: unknown
    ): error is { response?: { data?: { message?: string } } } => {
      return typeof error === "object" && error !== null && "response" in error;
    };

    if (
      (isAxiosError(shareholdersError) &&
        shareholdersError?.response?.data?.message === "LOGGED_OUT") ||
      (isAxiosError(tenureLengthError) &&
        tenureLengthError?.response?.data?.message === "LOGGED_OUT")
    ) {
      console.log("clearing storage due to logged out");
      clearLocalStorage();
      window.location.reload();
    }
  }

  // Mutation
  const updateMutation = useMutation<
    unknown,
    AxiosError<ErrorResponse>,
    { [key: string]: string | number }
  >({
    mutationFn: (formData) =>
      updateCompanyShareholder(selectedItem?.id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shareholders"] });
      toast({
        title: "ສຳເລັດ",
        description: "ແກ້ໄຂຂໍ້ມູນ ຄະນະອຳນວຍການ ສໍາເລັດແລ້ວ",
      });
      // navigate({ to: "..", replace: true });

      setEditTenureModal(false);
      setEditFormData({
        status: "",
        endDoc: null,
      });
    },
    onError: (error) => {
      console.log(error);
      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່",
        variant: "destructive",
      });
    },
  });

  // Effects
  useEffect(() => {
    if (tenureStatusRes?.data?.length > 0 && !search.activeStatus) {
      navigate({
        to: "/account-management/soe/board-of-directors",
        search: {
          activeStatus: tenureStatusRes?.data[1]?.id,
          page: search.page || 1,
        },
        replace: true,
      });
    }
  }, [tenureStatusRes, navigate, search]);

  // Functions
  const handleTabChange = (value: string) => {
    navigate({
      to: "/account-management/soe/board-of-directors",
      search: {
        activeStatus: value,
        page: 1,
      },
      replace: true,
    });
  };

  // Add pagination handler
  const handlePageChange = (page: number) => {
    navigate({
      search: {
        ...search,
        page,
      },
      replace: true,
    });
  };

  const calculateTenureStatus = (deployDate: string) => {
    const currentDate = new Date();
    const deploymentDate = new Date(deployDate);

    // Calculate the difference in years
    const yearDifference =
      currentDate.getFullYear() - deploymentDate.getFullYear();
    const monthDifference = currentDate.getMonth() - deploymentDate.getMonth();

    // Convert to total months for more accurate comparison
    const totalMonthsDifference = yearDifference * 12 + monthDifference;

    // Get tenure length from the response (default to 3 if not available)
    const maxTenureYears = tenureLength?.tenureLength || 3;

    // Convert tenure years to months
    const maxTenureMonths = maxTenureYears * 12;

    // If already passed the tenure length
    if (totalMonthsDifference >= maxTenureMonths) {
      // return "UNDEPLOYED";

      return (
        <Badge className="text-red-500 bg-[#DC26261A]">
          ຢູ່ໃນຊ່ວງປົດປະຈຳການ
        </Badge>
      );
    }

    // If in the last 3 months of tenure
    if (totalMonthsDifference >= maxTenureMonths - 3) {
      // return "SOON_UNDEPLOYED";

      return (
        <Badge className="text-[#CA8A04] bg-[#EAB3081A]">ໃກ້ຈະໝົດປະຈຳກັນ</Badge>
      );
    }

    // Otherwise, still actively deployed
    return (
      <Badge className="text-green-800 bg-[#16A34A1A]">ດຳເນີນການຢູ່</Badge>
    );
  };

  if (tenureStatusLoading || shareholdersLoading) {
    return <TableSkeleton />;
  }

  // Data
  const tenureStatus = tenureStatusRes?.data as {
    id: string;
    tenureStatus: string;
  }[];
  const shareholders = shareholdersRes?.data as CompanyBoards[];

  const tenureLength = tenureLengthRes?.data as {
    tenureLength: number;
  };

  return (
    <div className="space-y-[24px] ">
      <div className="text-[18px]">
        <h2 className="font-semibold text-zinc-950">
          ຂໍ້ມູນການຄະນະຜູ້ຖືຫຸ້ນ-ສະພາບໍລິຫານ
        </h2>
        <p className="text-zinc-500">
          ຕາຕະລາງຂໍ້ມູນການຄະນະຜູ້ຖືຫຸ້ນ-ສະພາບໍລິຫານຂອງວິສາຫະກິດ
        </p>

        <Separator className="my-6" />
      </div>

      <div className="flex justify-end w-full gap-4">
        <Button
          variant={"secondary"}
          onClick={() =>
            navigate({
              to: "/account-management/soe/board-of-directors/preview",
            })
          }
        >
          ສະແດງຕົວຢ່າງ
        </Button>
        {company_management.isWrite && (
          <Button
            onClick={() =>
              navigate({
                to: "/account-management/soe/board-of-directors/mutate",
                params: { id: companyId },
                search: {
                  id: undefined,
                  data: undefined,
                },
              })
            }
          >
            <PlusCircle className="mr-2" />
            ເພີ່ມຂໍ້ມູນ
          </Button>
        )}
      </div>

      <Tabs
        value={search.activeStatus}
        onValueChange={(value) => handleTabChange(value)}
        className="flex flex-col gap-y-12"
      >
        <TabsList className="flex flex-wrap items-start justify-start gap-2 bg-transparent">
          {tenureStatus?.slice(1)?.map((status) => (
            <TabsTrigger
              key={status.id}
              value={status.id}
              className="px-3 bg-gray-100 rounded-full text-black data-[state=active]:bg-green-500 data-[state=active]:text-white"
            >
              {status.tenureStatus}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Table className="border rounded-md">
        <TableHeader className="">
          <TableRow>
            {tableData.headers.map((header) => (
              <TableHead className="whitespace-nowrap" key={header}>
                {header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {shareholders?.map((item, index) => (
            <TableRow key={item?.id} className="cursor-pointer">
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/board-of-directors/$id",
                    params: {
                      id: item.id,
                    },
                    search: {
                      id: item.id,
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="whitespace-nowrap"
              >
                {index + 1}
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/board-of-directors/$id",
                    params: {
                      id: item.id,
                    },
                    search: {
                      id: item.id,
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="whitespace-nowrap"
              >
                <Avatar>
                  <AvatarImage
                    className="object-cover"
                    src={S3_BUCKET_LINK + item?.profileUrl}
                  />
                  <AvatarFallback>{item?.firstName[0]}</AvatarFallback>
                </Avatar>
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/board-of-directors/$id",
                    params: {
                      id: item.id,
                    },
                    search: {
                      id: item.id,
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="whitespace-nowrap"
              >
                {item?.gender === "MALE" ? "ທ່ານ" : "ທ່ານ ນາງ"}{" "}
                {item?.firstName} {item?.lastName}
              </TableCell>

              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/board-of-directors/$id",
                    params: {
                      id: item.id,
                    },
                    search: {
                      id: item.id,
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="whitespace-nowrap"
              >
                {item?.boardPosition?.boardPosition ?? "ບໍ່ມີຂໍ້ມູນ"}
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/board-of-directors/$id",
                    params: {
                      id: item.id,
                    },
                    search: {
                      id: item.id,
                      data: JSON.stringify(item),
                    },
                  })
                }
              >
                {item?.docFile
                  ? company_management.isDownload && (
                      <FileHandleViewer
                        fileUrl={item?.docFile}
                        showBtnPreview={true}
                      />
                    )
                  : "-"}
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/board-of-directors/$id",
                    params: {
                      id: item.id,
                    },
                    search: {
                      id: item.id,
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="whitespace-nowrap"
              >
                {formatDate({
                  date: item?.agreementDate,
                  format: "DD/MM/YYYY",
                })}
              </TableCell>
              <TableCell
                onClick={() =>
                  navigate({
                    to: "/account-management/soe/board-of-directors/$id",
                    params: {
                      id: item.id,
                    },
                    search: {
                      id: item.id,
                      data: JSON.stringify(item),
                    },
                  })
                }
                className="whitespace-nowrap"
              >
                {calculateTenureStatus(item?.agreementDate)}
              </TableCell>
              <TableCell>
                {company_management.isUpdate && (
                  <Button
                    onClick={() => {
                      setSelectedItem(item);
                      setEditTenureModal(true);
                      setEditFormData((prev) => ({
                        ...prev,
                        status: item?.tenureStatusId,
                        endDoc: item?.endDoc,
                      }));
                    }}
                    variant={"outline"}
                  >
                    ອັບເດດສະຖານະ
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination
        handlePageClick={handlePageChange}
        totalItems={shareholdersRes?.total || 0}
        itemsPerPage={40}
        currentPage={search.page}
      />

      <Dialog open={editTenureModal} onOpenChange={setEditTenureModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ຂໍ້ຕົກລົງ </DialogTitle>
            <DialogDescription>
              ກາລຸນາກວດສອບຂໍ້ມູນຂອງທ່ານໃຫ້ຖືກຕ້ອງ ກ່ອນກົດບັນທຶກ
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();

              if (!editFormData?.endDoc) {
                toast({
                  title: "ຫຼົ້ມເຫຼວ",
                  description: "ກະລຸນາອັບໂຫຼດເອກະສານແຕ່ງຕັ້ງ",
                  variant: "destructive",
                });

                return;
              }

              const formData = new FormData(e.target as HTMLFormElement);

              const payload = {
                tenureStatusId: editFormData.status,
                endDoc: editFormData.endDoc,
                endNo: formData.get("endNo") as string,
                endDate: formData.get("endDate") as string,
                id: selectedItem.id,
              };

              console.log("Payload", payload);

              updateMutation.mutate(payload);
            }}
            className="grid gap-4 py-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col items-start gap-4">
                <Label htmlFor="agreementNo" className="text-right">
                  ເລກທີແຕ່ງຕັ້ງ
                </Label>
                <Input
                  readOnly
                  id="agreementNo"
                  name="agreementNo"
                  type="text"
                  defaultValue={selectedItem?.agreementNo}
                />
              </div>
              <div className="flex flex-col items-start gap-4">
                <Label htmlFor="agreementDate" className="text-right">
                  ວັນທີແຕ່ງຕັ້ງ
                </Label>
                <Input
                  readOnly
                  id="agreementDate"
                  name="agreementDate"
                  type="date"
                  defaultValue={formatDate({
                    date: selectedItem?.agreementDate,
                    format: "YYYY-MM-DD",
                  })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="districtId">ສະຖານະ</Label>
              <Select
                name="tenureStatusId"
                onValueChange={(value) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    status: value,
                  }))
                }
                defaultValue={selectedItem?.tenureStatusId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="ເລືອກເມືອງ" />
                </SelectTrigger>
                <SelectContent>
                  {tenureStatus?.map((item) => (
                    <SelectItem value={item.id} key={item.id}>
                      {item?.tenureStatus}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col items-start gap-4">
                <Label htmlFor="endNo" className="text-right">
                  ເລກທີສິ້ນສຸດ <span className="text-red-500">*</span>
                </Label>
                <Input
                  required
                  id="endNo"
                  name="endNo"
                  type="text"
                  placeholder="ປ້ອນເລກທີສິ້ນສຸດ..."
                  defaultValue={selectedItem?.endNo || ""}
                />
              </div>
              <div className="flex flex-col items-start gap-4">
                <Label htmlFor="endDate" className="text-right">
                  ເລກທີສິ້ນສຸດ <span className="text-red-500">*</span>
                </Label>
                <Input
                  required
                  id="endDate"
                  name="endDate"
                  type="date"
                  defaultValue={
                    selectedItem?.endDate
                      ? formatDate({
                          date: selectedItem.endDate,
                          format: "YYYY-MM-DD",
                        })
                      : ""
                  }
                />
              </div>
            </div>

            <div>
              <FileUpload
                currentFile={
                  editFormData?.endDoc || selectedItem?.endDoc || undefined
                }
                onFileUpload={(file) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    endDoc: file,
                  }))
                }
              />
            </div>

            <DialogFooter>
              <DialogClose>
                <Button variant={"outline"} type="button">
                  ຍົກເລີກ
                </Button>
              </DialogClose>
              <Button type="submit">
                {updateMutation.isPending ? (
                  <Spinner className="text-white" />
                ) : (
                  <>
                    <Save className="mr-2" />
                    ບັນທຶກຂໍ້ມູນ
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const tableData = {
  headers: [
    "ລຳດັບ",
    "ຮູບໂປຣຟາຍ",
    "ຊື່ ແລະ ນາມສະກຸນ",
    "ຕຳແໜ່ງພາຍໃນວິສາຫະກິດ",
    "ເອກະສານຕິດຄັດ",
    "ວັນທີແຕ່ງຕັ້ງ",
    "ສະຖານະ",
    "ອັບເດດສະຖານະ",
  ],
};
